import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Whisper, Tooltip, Divider } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/legacy/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import FetchingSelect from '@/components/FetchingSelect';

import { Route, RoutePaginationFilterRequest } from '@/models/route.model';
import { getPagedRoutes } from '@/services/route.service';
import { getCitiesLookup } from '@/services/city.service';
import { Icon } from '@rsuite/icons';
import { MdAdd, MdEmojiEvents } from 'react-icons/md';
import { AchievementTypeEnum } from '@/models/achievement.model';
import { getCountriesLookup } from '@/services/country.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface RouteListProps {
  cityId?: number;
}
const RouteList : React.FC<RouteListProps> = ({cityId}) => {
  const [filterReq, setFilterReq] = useState<RoutePaginationFilterRequest>(new RoutePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RoutePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        cityId: cityId ?? req.cityId,
      };   
      const result: PagedResponseModel<Route> = await getPagedRoutes(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Routes:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/routes/new`, {state: {cityId}})}>
          Add Route
        </Button>

        <Stack spacing={6}>
          {!cityId && <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />}
          {!cityId && <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />}            
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >      
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="city.name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Length</HeaderCell>
          <Cell dataKey="length" />
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Achievement</HeaderCell>
          <Cell>
            {(rowData: Route) => (
              rowData.hasAchievement ? 
              <Icon as={MdEmojiEvents}/> : 
              <Whisper speaker={<Tooltip>Add Achievement</Tooltip>}>
                <IconButton size='xs' color="cyan" circle appearance="ghost" 
                  icon={<Icon as={MdAdd} />}
                  onClick={() => nav(`/achievements/new`, {state: {initValue:{
                    type: AchievementTypeEnum.Routes, 
                    name: `Complete Route: '${rowData.name}'`,
                    routeId: rowData.id,
                    supportingCityId: rowData.cityId,
                  }}})}
                />
              </Whisper>
            )}
          </Cell>
        </Column>        
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : Route) => (              
              <IconButton onClick={() => nav(`/routes/${rowData.id}`, {state: {cityId}})} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default RouteList;
